/**
 * Commit op-list — the DOM commit path's observability artifact
 * (the observe-only part of
 * `reconciliation-north-star/04-atomic-commit.md`).
 *
 * `DefaultBinder.onBind` fuses two things today: it decides *what* the
 * per-column commit will do (insert / recycle / remove each cell) and it
 * *applies* those decisions to the live DOM in one pass. This module
 * splits the first half out as a pure function so the commit's op-list
 * is inspectable before any DOM write — the gap it closes on the spine
 * observability board. It changes no behavior: `onBind` still applies
 * exactly as before; this only names what it is about to do.
 *
 * The op vocabulary mirrors `DomAction` in `dom-reconciler.ts`
 * (`cell-insert` / `cell-update` / `cell-remove`) and the `what` field
 * reuses `ChangeKind` from `@tento/lona/sheets`, so the artifact reads
 * as a lightweight `DomActionLog` keyed by the binder's row wire-string.
 *
 * IMPORTANT — this is the LIVE path's op-list, derived from
 * `Changeset.make` (positional edit-distance over the row-id sequence),
 * NOT from `diffScene` (key-based set diff). The two diff engines
 * disagree by construction: a positional row swap (`[A,B] → [A,C]`)
 * reads as a `replace` here but as `insert C + remove B` under
 * `diffScene`. Promoting the key-based diff onto the live path is the
 * separate migration (scene diff + classifier + `DomReconciler`); this artifact makes the
 * current, coarser commit observable so that gap is measurable.
 */

import type { Option } from "@tento-lona/sheets";
import type { Changeset } from "@tento-core/changeset";
import type { ChangeKind } from "@tento-lona/sheets/platform";

/**
 * The plan-level facts the op-list needs for one target cell: its row
 * wire-string key (`row.id.toString()`) and whether its render plan
 * carries a layout (a null layout means the cell is hidden, not laid
 * out). Callers project this off `RenderPlan.makeForRows` output so the
 * assembler stays DOM-free and headless-testable.
 */
export interface CommitCellInput {
  readonly key: string;
  readonly hasLayout: boolean;
}

/**
 * One recorded op the per-column commit will perform.
 *
 * `what.contentChanged` here means "the content element is (re)built" —
 * true for an inserted cell and for a positional `replace` (a recycled
 * cell whose row identity changed). A reused cell reports
 * `contentChanged: false` even though `onBind` re-binds its plugin value
 * in place; the coarse `Changeset` key (row identity, not value) cannot
 * see a value-only change. `what.bindingChanged` / `what.spanModeChanged`
 * always read `false` — deriving them needs the previous scene retained
 * (a live-path scene build), which this observe-only slice does not add.
 */
export type CommitOp =
  | { readonly tag: "cell-insert"; readonly key: string; readonly what: ChangeKind }
  | { readonly tag: "cell-update"; readonly key: string; readonly what: ChangeKind }
  | { readonly tag: "cell-remove"; readonly key: string };

export type CommitOpList = readonly CommitOp[];

/** One capped entry in the per-drain log's op sample. */
export interface CommitOpSample {
  readonly tag: CommitOp["tag"];
  readonly key: string;
  readonly content: boolean;
}

/** Bounded, JSON-safe summary emitted once per column commit. */
export interface CommitOpSummary {
  readonly ops: number;
  readonly inserts: number;
  readonly updates: number;
  readonly removes: number;
  /** Ops whose content element is (re)built (`what.contentChanged`). */
  readonly contentRebuilds: number;
  /** Reused cells that only re-bind layout/height (no content rebuild). */
  readonly layoutOnly: number;
  /** First `SAMPLE_CAP` ops verbatim — never the whole list. */
  readonly sample: readonly CommitOpSample[];
}

/** Cap on the per-drain op sample so the log payload stays bounded. */
const SAMPLE_CAP = 8;

/**
 * Assemble the commit op-list for one column's onBind pass.
 *
 * Replays the exact `Changeset` edit script `onBind` applies to its live
 * `$cells` array — `insert` splices a fresh cell in, `replace` recycles a
 * cell in place, `delete` splices a cell out — so the op-list names the
 * same cells the DOM pass touches, keyed by row identity. Pure: no DOM,
 * no allocation of scene nodes.
 *
 * @param changes    the `Changeset.make(oldRowIds, targetRowIds)` result
 *                   `onBind` already computed (reused, not recomputed).
 * @param oldRowIds  the `rid` of each currently-rendered cell, in order
 *                   (`null` for a never-bound cell).
 * @param cells      the target cells in target order, aligned to the
 *                   plans `onBind` applies.
 */
export function assembleCommitOps(
  changes: readonly Changeset[],
  oldRowIds: readonly Option<string>[],
  cells: readonly CommitCellInput[],
): CommitOpList {
  const inserted = new Set<string>();
  const replaced = new Set<string>();
  const removedKeys: string[] = [];

  // Replay against a mirror of the live `$cells` row-id sequence so a
  // `delete` resolves to the row-id occupying that slot at the moment
  // `onBind` splices it out — exact even when replaces/inserts shift
  // indices ahead of it (the changeset is pre-sorted deletes-descending).
  const working: Option<string>[] = [...oldRowIds];
  for (const change of changes) {
    switch (change.type) {
      case "insert": {
        const key = change.value ?? null;
        if (key != null) inserted.add(key);
        working.splice(change.index, 0, key);
        break;
      }
      case "replace": {
        const key = change.value ?? null;
        if (key != null) replaced.add(key);
        working[change.index] = key;
        break;
      }
      case "delete": {
        const gone = working[change.index];
        if (gone != null) removedKeys.push(gone);
        working.splice(change.index, 1);
        break;
      }
    }
  }

  const ops: CommitOp[] = [];
  for (const cell of cells) {
    if (inserted.has(cell.key)) {
      ops.push({ tag: "cell-insert", key: cell.key, what: kind(true, cell.hasLayout) });
    } else if (replaced.has(cell.key)) {
      ops.push({ tag: "cell-update", key: cell.key, what: kind(true, cell.hasLayout) });
    } else {
      ops.push({ tag: "cell-update", key: cell.key, what: kind(false, cell.hasLayout) });
    }
  }
  for (const key of removedKeys) ops.push({ tag: "cell-remove", key });
  return ops;
}

function kind(contentChanged: boolean, hasLayout: boolean): ChangeKind {
  return {
    contentChanged,
    layoutChanged: hasLayout,
    bindingChanged: false,
    spanModeChanged: false,
  };
}

/** Roll an op-list into the bounded payload the per-drain log emits. */
export function summarizeCommitOps(ops: CommitOpList): CommitOpSummary {
  let inserts = 0;
  let updates = 0;
  let removes = 0;
  let contentRebuilds = 0;
  let layoutOnly = 0;
  const sample: CommitOpSample[] = [];
  for (const op of ops) {
    const content = op.tag !== "cell-remove" && op.what.contentChanged;
    switch (op.tag) {
      case "cell-insert":
        inserts++;
        break;
      case "cell-update":
        updates++;
        if (!op.what.contentChanged && op.what.layoutChanged) layoutOnly++;
        break;
      case "cell-remove":
        removes++;
        break;
    }
    if (content) contentRebuilds++;
    if (sample.length < SAMPLE_CAP) {
      sample.push({ tag: op.tag, key: op.key, content });
    }
  }
  return {
    ops: ops.length,
    inserts,
    updates,
    removes,
    contentRebuilds,
    layoutOnly,
    sample,
  };
}
